import { MOBILE_SCREEN, useDeviceType } from '@travel/traveler-core/hooks';

import { ProviderListSort, ProviderPlanStatus } from 'favoriteList/constants/favoriteLists';
import paths from 'favoriteList/core/universalRouter/paths';
import { pushLocation } from 'favoriteList/store/__router/actions';
import {
  getPlanDataCount,
  getPlanList,
  getProviderDataCount,
  getProviderList,
} from 'favoriteList/store/providerPlan/selectors';

import { cx } from '@travel/utils';
import type { Query } from 'ProviderPlan-Types';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProviderNotice from '../ProviderNotice';
import styles from './providerPlanTab.module.scss';

const newOffset = (offset: number) => offset + 1;
const newLimit = (offset: number, limit: number, total: number) => Math.min(offset + limit, total);

type Props = { isDomestic: boolean; isPlanList: boolean; query: Query };

const ProviderPlanTab = (props: Props) => {
  const { isDomestic, isPlanList, query } = props;
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isSP = deviceType === MOBILE_SCREEN;

  const planResponse = useSelector(getPlanList);
  const planList = planResponse.body;

  const providerResponse = useSelector(getProviderList);
  const providerList = providerResponse.body;

  const providerDataCount = useSelector(getProviderDataCount);
  const planDataCount = useSelector(getPlanDataCount);

  const defaultTab = isPlanList ? ProviderPlanStatus.PLAN : ProviderPlanStatus.PROVIDER;
  const [currentTab, setCurrentTab] = useState(defaultTab);

  const total = isPlanList ? planDataCount : providerDataCount;
  const strongText = isPlanList ? 'プラン' : '件';

  const tabOnClickHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const providerPlanStatus = e.currentTarget.id as ProviderPlanStatus;
    const providerPath = isDomestic ? paths.domesticProvider.path : paths.overseasProvider.path;
    const planPath = isDomestic ? paths.domesticPlan.path : paths.overseasPlan.path;

    if (currentTab !== providerPlanStatus) {
      if (providerPlanStatus === ProviderPlanStatus.PROVIDER) {
        dispatch(
          pushLocation({
            pathname: providerPath,
          }),
        );
      } else if (providerPlanStatus === ProviderPlanStatus.PLAN) {
        dispatch(
          pushLocation({
            pathname: planPath,
          }),
        );
      }
      setCurrentTab(providerPlanStatus);
    }
  };

  return (
    <>
      {isSP ? (
        <div
          className={cx(
            styles.tabPaginationContainer,
            !isPlanList && styles.providerPaginationContainer,
          )}
          data-testid={isPlanList ? 'plan-container-sp' : 'hotel-container-sp'}
        >
          {/* tab */}
          <div className={styles.tabContainerSP}>
            <div className={styles.splitTabContainerSP}>
              <button
                data-testid="hotel-active-button-sp"
                onClick={tabOnClickHandler}
                id={ProviderPlanStatus.PROVIDER}
                className={isPlanList ? styles.inactiveContainerSP : styles.activeContainerSP}
              >
                <span className={isPlanList ? styles.inactiveTextSP : styles.activeTextSP}>
                  宿泊施設
                </span>
              </button>
              <button
                data-testid="plan-active-button-sp"
                id={ProviderPlanStatus.PLAN}
                onClick={tabOnClickHandler}
                className={isPlanList ? styles.activeContainerSP : styles.inactiveContainerSP}
              >
                <span className={isPlanList ? styles.activeTextSP : styles.inactiveTextSP}>
                  宿泊プラン
                </span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={styles.tabPaginationContainer}
          data-testid={isPlanList ? 'plan-container' : 'hotel-container'}
        >
          {/* tab */}
          <div className={styles.tabContainer}>
            <button
              data-testid="hotel-active-button"
              onClick={tabOnClickHandler}
              id={ProviderPlanStatus.PROVIDER}
              className={isPlanList ? styles.inactiveContainer : styles.activeContainer}
            >
              <span className={isPlanList ? styles.inactiveText : styles.activeText}>宿泊施設</span>
            </button>
            <button
              data-testid="plan-active-button"
              className={isPlanList ? styles.activeContainer : styles.inactiveContainer}
              id={ProviderPlanStatus.PLAN}
              onClick={tabOnClickHandler}
            >
              <span className={isPlanList ? styles.activeText : styles.inactiveText}>
                宿泊プラン
              </span>
            </button>
          </div>
          {query.f_sort === ProviderListSort.RATED_HIGH && <ProviderNotice />}
          {/* pagination */}
          <div className={styles.paginationContainer}>
            <p className={styles.paginationText}>
              {total}
              <strong className={styles.paginationStrongText}>
                {strongText}
                <>
                  中{' '}
                  {total > 0 ? (
                    <>
                      {isPlanList ? newOffset(planList?.offset) : newOffset(providerList?.offset)}～
                      {isPlanList
                        ? newLimit(planList?.offset, planList?.limit, planDataCount)
                        : newLimit(providerList?.offset, providerList?.limit, providerDataCount)}
                    </>
                  ) : (
                    <>{total}</>
                  )}{' '}
                  {strongText}表示
                </>
              </strong>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ProviderPlanTab;
